import "./serviceRequest.css";
import { useState, useEffect } from "react";
import RightSideModal from "../Modal/right-side-modal";
import Card from "../card";
import { FormProvider, useForm } from "react-hook-form";
import MultipleFileUploader from "../CustomUploader/MultipleFileUploader";
import {
  JPEG_PNG_EXTENSIONS,
  JPEG_PNG_MIME_TYPES,
} from "../CustomUploader/imageUploadConstants";
import SimpleSpinner from "../../components/loader";
import apiHelper from "../../utils/apiHelper";
import {
  DocumentEntityTypesEnum,
  DocumentService,
  DocumentTypesEnum,
  ServiceRequestService,
} from "@propertelligent/client-api";
import { v4 as uuidv4 } from "uuid";

interface VerifyServiceRequestValues {
  id: number;
  documents_verifyDocuments?: File[];
}

interface VerifyServiceRequestFormProps {
  isModalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  fetchData?: (page: number) => void;
  onServiceRequestVerified?: () => void;
  serviceRequestId: number | null;
}

const VerifyServiceRequestForm: React.FC<VerifyServiceRequestFormProps> = ({
  isModalOpen,
  setModalOpen,
  fetchData,
  onServiceRequestVerified,
  serviceRequestId,
}) => {
  const [resetCounter, setResetCounter] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [hasError, setHasError] = useState(false);
  const formId = `VerifyServiceRequestForm-${uuidv4()}`;

  const methods = useForm<VerifyServiceRequestValues>({
    mode: "onChange",
    defaultValues: {
      id: null as any,
      documents_verifyDocuments: [],
    },
  });
  const { formState: { isDirty, isValid } } = methods;

  // Opens the modal immediately on click, then fetches the service request details inside it --
  // rather than fetching first and only opening once data arrives. ActionMenu's dropdown closes
  // synchronously on any click, so a loading state on the (now-closed) menu item is invisible;
  // showing the loader inside the already-open modal is the only place it's actually seen.
  useEffect(() => {
    if (!isModalOpen || !serviceRequestId) {
      setResponse(null);
      setHasError(false);
      return;
    }

    setIsLoadingDetails(true);
    setHasError(false);
    apiHelper(
      ServiceRequestService.getApiServiceRequest1,
      { showNotification: false },
      serviceRequestId
    )
      .then((res) => {
        if (res?.data?.success) {
          setResponse(res.data);
          methods.reset({
            id: res.data.success?.id || res.data.id,
            documents_verifyDocuments: [],
          });
        } else {
          setHasError(true);
        }
      })
      .catch((err) => {
        console.error("Error fetching service request details:", err);
        setHasError(true);
      })
      .finally(() => {
        setIsLoadingDetails(false);
      });
  }, [isModalOpen, serviceRequestId, methods]);

  const onSubmit = async (data: VerifyServiceRequestValues) => {
    try {
      setIsLoading(true);

      // Call the verify API
      const verifyResponse = await apiHelper(
        ServiceRequestService.putApiServiceRequestVerify,
        {
          successMessage: "Service request verified successfully",
          showNotification: true,
        },
        data.id
      );

      // Check if verification was successful
      if (!verifyResponse?.data?.success) {
        throw new Error("Verification failed");
      }

      // If there are documents to upload/images, upload them
      if (
        data.documents_verifyDocuments &&
        data.documents_verifyDocuments.length > 0
      ) {
        const uploadPromises = data.documents_verifyDocuments.map((file) => {
          const apiDocObj = {
            EntityTypeId: DocumentEntityTypesEnum.ServiceRequest,
            EntityId: data.id,
            DocumentTypeId: DocumentTypesEnum.BeforeImage,
            IsMain: false,
            FileName: file.name,
            Document: file,
          };
          return apiHelper(
            DocumentService.postApiDocument,
            { showNotification: false },
            apiDocObj
          );
        });

        const uploadResults = await Promise.all(uploadPromises);
      } else {
        console.log("No documents to upload");
      }

      methods.reset();
      setResetCounter((prev) => prev + 1);
      setModalOpen(false);
      if (fetchData) fetchData(1);
      if (onServiceRequestVerified) onServiceRequestVerified();
    } catch (error) {
      // Show error to user
      alert("Failed to verify service request. Please check console for details.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    methods.reset();
    setResetCounter((prev) => prev + 1);
    setModalOpen(false);
  };

  return (
    <RightSideModal
      isOpen={isModalOpen}
      onClose={handleCancel}
      title="Verify Service Request"
      buttonlabel={isLoading ? "Verifying..." : "Verify"}
      // Deliberately not gated on isDirty: Verify is a status-transition action, not a
      // data edit -- the image upload field is optional, so a user must be able to click
      // Verify as soon as the service-request details finish loading, without first
      // touching any field. (The generic Save-button "disabled until dirty" convention
      // from ui-consistency.md applies to edit forms, not this kind of action button.)
      isSubmitDisabled={isLoading || isLoadingDetails || !response || !isValid}
      isDirty={isDirty}
      formId={formId}
      maxWidth={550}
    >
      <Card className="no-bg">
        {isLoadingDetails ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "60vh" }}
          >
            <SimpleSpinner myClassName="Icon50" />
          </div>
        ) : hasError ? (
          <div className="p-4 text-center">
            <div className="p-3 mb-3 d-flex flex-column align-items-center justify-content-center sr-error-container">
              <div className="fw-600 mb-1 sr-error-title">
                Failed to Load Details
              </div>
              <div className="small text-secondary">
                Could not retrieve service request details. Please close this modal and try again.
              </div>
            </div>
          </div>
        ) : (
          <FormProvider {...methods}>
            <form id={formId} onSubmit={methods.handleSubmit(onSubmit)}>
              <div className="row">
                <div className="col-md-12">
                  <div className="form-group mb-4">
                     <label className="fw-600 mb-2">Service Request Details</label>
                    <div className="p-3 sr-details-container">
                      {response?.success?.formatted?.ServiceType && (
                        <div className="mb-2">
                          <strong>Service Type:</strong>{" "}
                          {response.success.formatted.ServiceType}
                        </div>
                      )}
                      {response?.success?.formatted?.ServiceSubType && (
                        <div className="mb-2">
                          <strong>Sub Type:</strong>{" "}
                          {response.success.formatted.ServiceSubType}
                        </div>
                      )}
                      {response?.success?.description && (
                        <div className="mb-2">
                          <strong>Description:</strong>{" "}
                          {response.success.description}
                        </div>
                      )}
                      {response?.success?.streetAddress && (
                        <div className="mb-2">
                          <strong>Address:</strong>{" "}
                          {response.success.streetAddress}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-md-12">
                  <MultipleFileUploader
                    name="documents_verifyDocuments"
                    uploaderId="verifyServiceRequestDocuments"
                    label="Upload Verification Images"
                    disabled={isLoading}
                    allowedFormats={[...JPEG_PNG_MIME_TYPES]}
                    allowedExtensions={[...JPEG_PNG_EXTENSIONS]}
                    maxSizeMB={1}
                    maxFiles={5}
                    resetTrigger={resetCounter}
                    uploadertext="Upload Files"
                    uploadertypeslabel="Max. size 1MB. JPEG, PNG (Max 5 files)"
                    convertImagesToPng={false}
                  />
                </div>
              </div>
            </form>
          </FormProvider>
        )}
      </Card>
    </RightSideModal>
  );
};

export default VerifyServiceRequestForm;
